// src/context/ChatContext.tsx
import React, { createContext, useContext, useCallback, useMemo, useRef } from 'react';

// Simple EventEmitter-like class that mimics the required interface
class SimpleEventEmitter {
  private listeners: Set<() => void> = new Set();

  // Method to add a listener function
  listen(callback: () => void): () => void {
    if (typeof callback !== 'function') {
      // Return a no-op unsubscribe function
      return () => {};
    }

    this.listeners.add(callback);

    // Return a function to unsubscribe
    return () => {
      this.listeners.delete(callback);
    };
  }

  // Method to trigger all listener functions
  emit(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in event listener:', error);
      }
    });
  }
}

// Define the context shape
interface ChatContextType {
  refreshHistory: () => void;
  triggerHistoryRefresh: SimpleEventEmitter;
  _registerRefreshFunction?: (fn: () => void) => () => void; // optional internal
}

// Create a stable instance of the event emitter
const historyRefreshEmitter = new SimpleEventEmitter();

// Create a no-op refresh function
const noopRefresh = () => {};

// Create context with default values
const ChatContext = createContext<ChatContextType>({
  refreshHistory: noopRefresh,
  triggerHistoryRefresh: historyRefreshEmitter,
});

// Custom hook for using the context
export const useChatContext = () => useContext(ChatContext);

// Provider component
export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use a ref to store the refresh function to avoid state updates during render
  const refreshFunctionRef = useRef<(() => void) | null>(null);

  // The actual refresh function that will be called by components directly
  const refreshHistory = useCallback(() => {
    if (refreshFunctionRef.current) {
      refreshFunctionRef.current();
    } else {
      // No refresh function registered yet
    }
  }, []);

  // Set up the registration function - this will be exported but not part of the context
  const registerRefreshFunction = useCallback((fn: () => void) => {
    refreshFunctionRef.current = fn;
    return () => {
      refreshFunctionRef.current = null;
    };
  }, []);

  // Register the refresh function as a listener on the emitter
  React.useEffect(() => {
    if (refreshFunctionRef.current) {
      // Add the current refresh function as a listener
      const unsubscribe = historyRefreshEmitter.listen(() => {
        if (refreshFunctionRef.current) {
          refreshFunctionRef.current();
        }
      });

      // Clean up the listener when the component unmounts
      return unsubscribe;
    }
  }, []);

  // The context value - use useMemo to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      refreshHistory,
      triggerHistoryRefresh: historyRefreshEmitter,
    }),
    [refreshHistory]
  );

  // Add registerRefreshFunction to the internal value (not part of public context)
  const internalValue = {
    ...contextValue,
    _registerRefreshFunction: registerRefreshFunction,
  };

  return <ChatContext.Provider value={internalValue}>{children}</ChatContext.Provider>;
};

// This will be used by components that want to provide a refreshHistory implementation
export const useRegisterHistoryRefresh = (refreshFunction: () => void) => {
  const context = useChatContext();

  // Register the refresh function when the component mounts
  React.useEffect(() => {
    if (refreshFunction && context._registerRefreshFunction) {
      const unregister = context._registerRefreshFunction(refreshFunction);

      // Clean up when the component unmounts
      return unregister;
    }
  }, [refreshFunction, context]);

  return context;
};
